import { useState } from 'react'
import { checkManual, createManualPayment, type ManualPayment } from '@core/manual'
import type { FamilySession } from '@core/session'
import { Button, Card, ErrorNote, Field, Screen } from '@design/components'
import { RoutingPicker } from '@features/verification/RoutingPicker'
import { PhraseLine } from './PhraseLine'

/**
 * Adding a payment by hand — spec.md §354.
 *
 * The typed sentence sits on top and the form underneath, as §354 asks. The
 * order of construction was the other way round on purpose: the form is the
 * part that has to exist and has to be good, and the parser is a shortcut over
 * something that already works. It fills fields in and saves nothing, so a
 * sentence it cannot read costs one glance and never a correction.
 *
 * Everything entered here is verified by definition — a person typed it while
 * looking at it — which is what allows an IBAN to be stored at all.
 */
export function AddScreen({
  session,
  onDone,
  onClose,
}: {
  session: FamilySession
  onDone: () => void
  onClose: () => void
}) {
  const [form, setForm] = useState<ManualPayment>({
    spaceId: session.spaces[0]?.id ?? '',
    description: '',
    amount: '',
    dueDate: '',
    category: null,
    iban: '',
    reference: '',
    notes: '',
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [touched, setTouched] = useState(false)

  const problems = checkManual(form)
  const problemFor = (field: keyof ManualPayment) =>
    touched ? problems.find((p) => p.field === field)?.message : undefined

  const set = (field: keyof ManualPayment, value: string | null) =>
    setForm((f) => ({ ...f, [field]: value }) as ManualPayment)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setTouched(true)
    if (problems.length > 0) return
    setBusy(true)
    setError('')
    try {
      await createManualPayment(form, session.userId)
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Er ging iets mis.')
      setBusy(false)
    }
  }

  return (
    <Screen className="flex flex-col">
      <header className="shrink-0 flex items-center justify-between mb-3">
        <button onClick={onClose} className="text-muted py-2 px-1 -ml-1">
          Annuleren
        </button>
        <h1 className="text-lg font-semibold">Toevoegen</h1>
        <span className="w-16" />
      </header>

      <form onSubmit={submit} className="flex-1 scroll-area">
        <PhraseLine
          onFill={({ description, amount, dueDate }) =>
            setForm((f) => ({
              ...f,
              // Only what it actually read. An empty result must never wipe
              // something you already typed by hand.
              description: description || f.description,
              amount: amount || f.amount,
              dueDate: dueDate || f.dueDate,
            }))
          }
        />

        <Card className="mb-4">
          <Field
            label="Aan wie"
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            placeholder="Metro, de gemeente, de loodgieter…"
            hint={problemFor('description')}
            autoFocus
          />
          <Field
            label="Bedrag"
            inputMode="decimal"
            value={form.amount}
            onChange={(e) => set('amount', e.target.value)}
            placeholder="1.289,70"
            hint={problemFor('amount')}
          />
          <Field
            label="Vervaldatum"
            type="date"
            value={form.dueDate}
            onChange={(e) => set('dueDate', e.target.value)}
            hint={problemFor('dueDate')}
          />
        </Card>

        <Card className="mb-4">
          <RoutingPicker
            spaces={session.spaces}
            spaceId={form.spaceId}
            category={form.category}
            onSpace={(id) => setForm((f) => ({ ...f, spaceId: id, category: null }))}
            onCategory={(c) => set('category', c)}
          />
        </Card>

        <Card className="mb-4">
          <Field
            label="Rekeningnummer"
            value={form.iban}
            onChange={(e) => set('iban', e.target.value)}
            placeholder="BE68 5390 0754 7034"
            autoCapitalize="characters"
            autoCorrect="off"
            hint={problemFor('iban') ?? 'Mag leeg blijven.'}
          />
          <Field
            label="Mededeling"
            value={form.reference}
            onChange={(e) => set('reference', e.target.value)}
            placeholder="+++090/9337/55493+++ of vrije tekst"
            hint={problemFor('reference') ?? undefined}
          />
          <Field
            label="Notitie"
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
            hint="Alleen voor jezelf."
          />
        </Card>

        <ErrorNote>{error}</ErrorNote>

        <div className="pb-4">
          <Button type="submit" size="large" disabled={busy}>
            {busy ? 'Bezig…' : 'Toevoegen'}
          </Button>
        </div>
      </form>
    </Screen>
  )
}
